import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Save, Plus, Trash2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

const CATEGORIES = [
  { value: 'cleaning', label: 'Temizlik' },
  { value: 'fixture', label: 'Demirbas Kullanimi' },
  { value: 'test', label: 'Deneme / Test' },
  { value: 'staff', label: 'Personel Tuketimi' },
  { value: 'guest', label: 'Misafir Tuketimi' },
  // Organizasyonlarda bedeli tahsil edilen tuketim — maliyeti gider ama
  // karsiliginda gelir var, ayri izlenmesi isteniyor.
  { value: 'paid', label: 'Ucretli Tuketim' },
  { value: 'other', label: 'Diger' },
];

const CONSUMABLE_TYPES = ['raw_material', 'semi_finished', 'finished', 'trade_goods', 'consumable'];

function CategoryCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = CATEGORIES.filter(c =>
    trNorm(c.label).includes(trNorm(query)) ||
    trNorm(c.value).includes(trNorm(query))
  );

  const selectedLabel = CATEGORIES.find(c => c.value === value)?.label || value || '';

  const showCreate = query.trim() && !CATEGORIES.some(
    c => trNorm(c.label) === trNorm(query) || trNorm(c.value) === trNorm(query)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between mt-1 border-[#d0c4b0] bg-white text-[#1a1916] hover:bg-white hover:border-[#b0a090] font-normal h-10"
        >
          {selectedLabel
            ? <span>{selectedLabel}</span>
            : <span className="text-[#8a7e70]">Kategori secin veya yazin...</span>
          }
          <ChevronsUpDown size={14} className="ml-2 shrink-0 text-[#8a7e70]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Ara veya yeni kategori yaz..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {!showCreate && filtered.length === 0 && (
              <CommandEmpty>Sonuc bulunamadi</CommandEmpty>
            )}
            <CommandGroup>
              {filtered.map(c => (
                <CommandItem
                  key={c.value}
                  value={c.value}
                  onSelect={() => {
                    onChange(c.value);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <Check size={14} className={`mr-2 shrink-0 ${value === c.value ? 'opacity-100' : 'opacity-0'}`} />
                  {c.label}
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem
                  value={`__create__${query}`}
                  onSelect={() => {
                    onChange(query.trim());
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <Plus size={14} className="mr-2 shrink-0 text-[#3d6e4e]" />
                  <span className="text-[#3d6e4e]">Ekle: "{query.trim()}"</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ProductCombobox({ value, allProducts, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = allProducts.filter(p =>
    trNorm(p.name).includes(trNorm(query)) ||
    trNorm(p.code).includes(trNorm(query))
  ).slice(0, 60);

  const selectedProduct = allProducts.find(p => p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between border-[#d0c4b0] bg-white text-[#1a1916] hover:bg-white hover:border-[#b0a090] font-normal text-sm h-9"
        >
          {selectedProduct
            ? <span className="truncate text-left">{selectedProduct.name} <span className="text-[#8a7e70]">({selectedProduct.code})</span></span>
            : <span className="text-[#8a7e70]">Urun ara...</span>
          }
          <ChevronsUpDown size={12} className="ml-2 shrink-0 text-[#8a7e70]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Ad veya kod ile ara..." value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>Urun bulunamadi</CommandEmpty>
            <CommandGroup>
              {filtered.map(p => (
                <CommandItem
                  key={p.id}
                  value={p.id}
                  onSelect={() => {
                    onChange(p.id);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <Check size={12} className={`mr-2 shrink-0 ${value === p.id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{p.name}</span>
                  <span className="ml-1 text-xs text-[#8a7e70] shrink-0">({p.code})</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ConsumptionNew() {
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/production-orders', { state: { tab: 'consumptions' } });
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [warehouses, setWarehouses] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  // `name`: serbest metin ad — kategori sabit listeden geliyor ve tek
  // basina ayirt edici degil ("Personel Tuketimi" adli on kayit gibi).
  // `consumption_date`: tuketimin GERCEKLESTIGI gun; stok hareketi buna
  // yazilir, onay gunune degil.
  const [form, setForm] = useState({
    warehouse_id: '', category: '', name: '', description: '',
    consumption_date: new Date().toLocaleDateString('en-CA'),
    // Sadece bilgi — hicbir hesabi etkilemez (bkz. sunucu tarafi)
    portion_count: '',
  });
  const [items, setItems] = useState([{ product_card_id: '', quantity: '', unit: '' }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
    // per_page 500 idi; sistemde 1450 urun var ve liste sessizce kesiliyordu
    api.get('/products', { params: { per_page: 5000 } }).then(r => {
      const products = (r.data.items || r.data).filter(p => CONSUMABLE_TYPES.includes(p.type));
      setAllProducts(products);
    }).catch(() => {});

    if (isEdit) {
      api.get(`/consumptions/${id}`).then(r => {
        const c = r.data;
        setForm({
          warehouse_id: c.warehouse_id, category: c.category,
          name: c.name || '', description: c.description,
          portion_count: c.portion_count != null ? String(c.portion_count) : '',
          // Eski kayitlarda tarih yok — kayit gunune dus
          consumption_date: (c.consumption_date || c.created_at || '').slice(0, 10),
        });
        setItems(c.items.map(i => ({ product_card_id: i.product_card_id, quantity: String(i.quantity), unit: i.unit })));
      }).catch(() => toast.error('Tuketim yuklenemedi'));
    }
  }, [id, isEdit]);

  const setField = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const addItem = () => setItems(p => [...p, { product_card_id: '', quantity: '', unit: '' }]);

  const removeItem = (idx) => setItems(p => p.filter((_, i) => i !== idx));

  const updateItem = (idx, field, value) => {
    setItems(p => p.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'product_card_id') {
        const product = allProducts.find(p => p.id === value);
        updated.unit = product?.unit || '';
      }
      return updated;
    }));
  };

  const handleSubmit = async () => {
    if (!form.warehouse_id || !form.category || !form.description.trim()) {
      toast.error('Depo, kategori ve aciklama zorunlu');
      return;
    }
    const validItems = items.filter(i => i.product_card_id && i.quantity);
    if (validItems.length === 0) {
      toast.error('En az bir urun ekleyin');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        // Bos birakilabilir; bos string yerine null gitsin
        portion_count: form.portion_count === '' ? null : parseFloat(form.portion_count),
        items: validItems.map(i => ({ product_card_id: i.product_card_id, quantity: parseFloat(i.quantity), unit: i.unit }))
      };
      if (isEdit) {
        await api.patch(`/consumptions/${id}`, payload);
        toast.success('Tuketim guncellendi');
      } else {
        await api.post('/consumptions', payload);
        toast.success('Tuketim olusturuldu');
      }
      navigate('/erp/production-orders', { state: { tab: 'consumptions' } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1a1916] tracking-tight">
          {isEdit ? 'Tuketimi Duzenle' : 'Yeni Tuketim'}
        </h1>
      </div>

      <Card className="bg-[#faf7f2] border-[#d0c4b0] mb-6">
        <CardHeader><CardTitle className="text-base text-[#1a1916]">Tuketim Bilgileri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#3F3E39] font-medium">Depo *</Label>
            <Select value={form.warehouse_id} onValueChange={v => setField('warehouse_id', v)}>
              <SelectTrigger className="mt-1 border-[#d0c4b0] bg-white">
                <SelectValue placeholder="Depo secin" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#3F3E39] font-medium">Kategori *</Label>
            <CategoryCombobox value={form.category} onChange={v => setField('category', v)} />
          </div>
          <div>
            <Label className="text-[#3F3E39] font-medium">Ad *</Label>
            <Input value={form.name} onChange={e => setField('name', e.target.value)}
              placeholder="Personel yemegi, sef demosu..."
              className="mt-1 border-[#d0c4b0] bg-white" data-testid="consumption-name" />
          </div>
          <div>
            <Label className="text-[#3F3E39] font-medium">Porsiyon Adedi</Label>
            <Input type="number" step="any" min="0" value={form.portion_count}
              onChange={e => setField('portion_count', e.target.value)}
              placeholder="orn. 42"
              className="mt-1 border-[#d0c4b0] bg-white" data-testid="consumption-portion" />
            <p className="text-xs text-[#8a7e70] mt-1">
              Kaç kişilik/porsiyon yapıldı. Yalnızca bilgi — stok ve maliyeti etkilemez.
            </p>
          </div>
          <div>
            <Label className="text-[#3F3E39] font-medium">Tarih *</Label>
            <Input type="date" value={form.consumption_date}
              onChange={e => setField('consumption_date', e.target.value)}
              max={new Date().toLocaleDateString('en-CA')}
              className="mt-1 border-[#d0c4b0] bg-white" data-testid="consumption-date" />
            <p className="text-xs text-[#8a7e70] mt-1">
              Stok hareketi bu tarihe işlenir. Varsayılan bugündür.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#3F3E39] font-medium">Aciklama *</Label>
            <textarea
              value={form.description}
              onChange={e => setField('description', e.target.value)}
              rows={3}
              placeholder="Tuketim gerekcesini yazin..."
              className="mt-1 w-full rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-sm text-[#1a1916] placeholder:text-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#2e2d29] resize-none"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#faf7f2] border-[#d0c4b0] mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base text-[#1a1916]">Tuketilen Urunler</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2">
          {items.length > 0 && (
            <div className="hidden sm:flex gap-2 px-3 pb-1">
              <div className="flex-1 min-w-0 text-xs font-medium text-[#8a7e70] uppercase tracking-wide">Urun</div>
              <div className="w-24 text-xs font-medium text-[#8a7e70] uppercase tracking-wide text-right">Miktar</div>
              <div className="w-20 text-xs font-medium text-[#8a7e70] uppercase tracking-wide">Birim</div>
              <div className="w-8" />
            </div>
          )}
          {items.map((item, idx) => (
            <div key={idx} className="flex flex-col sm:flex-row gap-2 p-3 bg-white border border-[#e8dfd0] rounded-lg">
              <div className="flex-1 min-w-0">
                <Label className="text-xs text-[#8a7e70] mb-1 block sm:hidden">Urun</Label>
                <ProductCombobox
                  value={item.product_card_id}
                  allProducts={allProducts}
                  onChange={v => updateItem(idx, 'product_card_id', v)}
                />
              </div>
              <div className="flex gap-2 items-end sm:items-center">
                <div>
                  <Label className="text-xs text-[#8a7e70] mb-1 block sm:hidden">Miktar</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity}
                    onChange={e => updateItem(idx, 'quantity', e.target.value)}
                    className="border-[#d0c4b0] bg-white text-right w-24"
                    style={{ MozAppearance: 'textfield' }}
                    onWheel={e => e.target.blur()}
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#8a7e70] mb-1 block sm:hidden">Birim</Label>
                  <Input
                    value={item.unit}
                    onChange={e => updateItem(idx, 'unit', e.target.value)}
                    className="border-[#d0c4b0] bg-white w-20"
                    placeholder="birim"
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeItem(idx)}
                  className="text-[#7a3535] hover:bg-[#7a3535]/10 p-2 shrink-0"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-center py-6 text-[#8a7e70] text-sm">Henuz urun eklenmedi</p>
          )}
          <button
            type="button"
            onClick={addItem}
            className="w-full mt-1 flex items-center justify-center gap-2 rounded-lg border border-dashed border-[#c4b89e] py-2.5 text-sm text-[#8a7e70] hover:border-[#a09080] hover:text-[#3F3E39] hover:bg-[#f5f0e8] transition-colors"
          >
            <Plus size={15} />
            Urun Ekle
          </button>
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/erp/production-orders')} className="border-[#d0c4b0] text-[#3F3E39]">
          Iptal
        </Button>
        <Button onClick={handleSubmit} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
          <Save size={16} className="mr-2" /> {isEdit ? 'Guncelle' : 'Kaydet'}
        </Button>
      </div>
    </div>
  );
}
